/**
 * Persistent text layer helpers.
 * Pure conversions between EditorLayer text payload, floats, and raster buffers.
 * Manifest v3: runs[] + block defaults; v2 flat params migrate to one synthetic run.
 */

import {
  createFloat,
  normalizeRotationDeg,
  pointInFloat,
  rasterizeFloatToDocumentPixels,
  resolveFloatScaleAxes,
} from './imageEditorFloat.js';
import {
  applyWholeObjectPatchToRuns,
  cloneTextRuns,
  flattenRunsToText,
  normalizeEditorTextParams,
  normalizeTextRun,
  normalizeTextRuns,
  renderTextToImageData,
  resolveEditorTextContent,
  synthesizeRunsFromFlatParams,
} from './imageEditorText.js';

export const LAYER_KIND_RASTER = 'raster';
export const LAYER_KIND_TEXT = 'text';

/**
 * @typedef {import('./imageEditorFloat').EditorTextParams} EditorTextParams
 * @typedef {import('./imageEditorText').EditorTextRun} EditorTextRun
 */

/**
 * @typedef {{
 *   cx: number,
 *   cy: number,
 *   scale: number,
 *   scaleX: number,
 *   scaleY: number,
 *   rotationDeg: number,
 * }} EditorTextTransform
 */

/**
 * @typedef {{
 *   params: EditorTextParams,
 *   runs: EditorTextRun[],
 *   transform: EditorTextTransform,
 *   naturalWidth: number,
 *   naturalHeight: number,
 * }} EditorTextLayerContent
 */

/**
 * @param {unknown} layer
 * @returns {boolean}
 */
export function isEditorTextLayer(layer) {
  return Boolean(
    layer &&
      typeof layer === 'object' &&
      layer.kind === LAYER_KIND_TEXT &&
      layer.text &&
      typeof layer.text === 'object',
  );
}

/**
 * Normalize + deep-clone text layer content.
 * Preserves runs[] (v3). Flat v2 payloads become one synthetic run.
 *
 * @param {Partial<EditorTextLayerContent>|null|undefined} content
 * @returns {EditorTextLayerContent}
 */
export function cloneEditorTextLayerContent(content) {
  if (!content || typeof content !== 'object') {
    throw new Error('Text layer content is required.');
  }
  const transform = content.transform || {};
  const rawParams =
    content.params && typeof content.params === 'object' ? content.params : {};
  const defaults = normalizeEditorTextParams(rawParams);

  const rawRuns = Array.isArray(content.runs)
    ? content.runs
    : Array.isArray(rawParams.runs)
      ? rawParams.runs
      : null;

  const runs =
    rawRuns && rawRuns.length
      ? normalizeTextRuns(rawRuns, defaults)
      : synthesizeRunsFromFlatParams(defaults);

  // Single-run (v2 + whole-object 2a): params are authoritative so callers that
  // patch params.text/color without touching runs still round-trip. Multi-run:
  // runs stay source of truth unless params.text diverges (Content-field edit).
  let nextRuns = runs;
  if (nextRuns.length === 1) {
    nextRuns = [
      normalizeTextRun(
        {
          text: defaults.text || nextRuns[0].text,
          bold: defaults.bold,
          italic: defaults.italic,
          color: defaults.color,
          fontFamily: defaults.fontFamily,
          fontSize: defaults.fontSize,
        },
        defaults,
      ),
    ];
  } else {
    const flatFromRuns = flattenRunsToText(nextRuns);
    if (defaults.text !== flatFromRuns) {
      nextRuns = applyWholeObjectPatchToRuns(nextRuns, defaults, {
        text: defaults.text,
      });
    }
  }

  const flatText = flattenRunsToText(nextRuns);
  const params = {
    ...defaults,
    text: flatText || defaults.text,
    bold: Boolean(defaults.bold),
    italic: Boolean(defaults.italic),
  };

  const axes = resolveFloatScaleAxes(transform);
  return {
    params,
    runs: cloneTextRuns(nextRuns),
    transform: {
      cx: Number.isFinite(transform.cx) ? transform.cx : 0,
      cy: Number.isFinite(transform.cy) ? transform.cy : 0,
      // Keep legacy `scale` for older readers; additive scaleX/scaleY (no
      // manifest version bump — migrate missing axis from `scale` on load).
      scale: axes.scale,
      scaleX: axes.scaleX,
      scaleY: axes.scaleY,
      rotationDeg: normalizeRotationDeg(
        transform.rotationDeg == null ? 0 : transform.rotationDeg,
      ),
    },
    naturalWidth: Math.max(1, Math.round(Number(content.naturalWidth) || 1)),
    naturalHeight: Math.max(1, Math.round(Number(content.naturalHeight) || 1)),
  };
}

/**
 * @param {import('./imageEditorFloat').EditorFloat} float
 * @returns {EditorTextLayerContent}
 */
export function editorTextLayerContentFromFloat(float) {
  if (!float?.textParams) {
    throw new Error('Text float is missing textParams.');
  }
  const resolved = resolveEditorTextContent(float.textParams);
  const axes = resolveFloatScaleAxes(float);
  return cloneEditorTextLayerContent({
    params: {
      text: resolved.params.text,
      fontFamily: resolved.params.fontFamily,
      fontSize: resolved.params.fontSize,
      color: resolved.params.color,
      align: resolved.params.align,
      bold: resolved.params.bold,
      italic: resolved.params.italic,
    },
    runs: resolved.runs,
    transform: {
      cx: float.cx,
      cy: float.cy,
      scale: axes.scale,
      scaleX: axes.scaleX,
      scaleY: axes.scaleY,
      rotationDeg: float.rotationDeg,
    },
    naturalWidth: float.width,
    naturalHeight: float.height,
  });
}

/**
 * @param {{ kind?: string, text?: EditorTextLayerContent }} layer
 * @returns {import('./imageEditorFloat').EditorFloat|null}
 */
export function editorTextLayerToFloat(layer) {
  if (!isEditorTextLayer(layer)) return null;
  const content = cloneEditorTextLayerContent(layer.text);
  const textParams = {
    ...content.params,
    runs: cloneTextRuns(content.runs),
  };
  const rendered = renderTextParamsToImageData(textParams);
  if (!rendered) return null;
  return createFloat({
    imageData: rendered,
    cx: content.transform.cx,
    cy: content.transform.cy,
    scale: content.transform.scale,
    scaleX: content.transform.scaleX,
    scaleY: content.transform.scaleY,
    rotationDeg: content.transform.rotationDeg,
    origin: 'text',
    labelHint: content.params.text,
    textParams,
  });
}

/**
 * @param {EditorTextParams} params
 * @param {(p: EditorTextParams) => ImageData|null} [renderFn]
 * @returns {ImageData|null}
 */
export function renderTextParamsToImageData(params, renderFn) {
  if (typeof renderFn === 'function') return renderFn(params);
  return renderTextToImageData(params);
}

/**
 * @param {{ kind?: string, text?: EditorTextLayerContent }} layer
 * @param {number} docWidth
 * @param {number} docHeight
 * @param {(p: EditorTextParams) => ImageData|null} [renderFn]
 * @returns {ImageData|null}
 */
export function renderEditorTextLayerToDocumentPixels(
  layer,
  docWidth,
  docHeight,
  renderFn,
) {
  const float = editorTextLayerToFloat(layer);
  if (!float) return null;
  if (typeof renderFn === 'function' && float.textParams) {
    const pixels = renderFn(float.textParams);
    if (pixels) {
      float.imageData = pixels;
      float.width = pixels.width;
      float.height = pixels.height;
    }
  }
  return rasterizeFloatToDocumentPixels(float, docWidth, docHeight);
}

/**
 * @param {import('./imageEditorDocument').EditorLayer} layer
 * @param {number} canvasX
 * @param {number} canvasY
 * @returns {boolean}
 */
export function hitTestEditorTextLayer(layer, canvasX, canvasY) {
  const float = editorTextLayerToFloat(layer);
  if (!float) return false;
  return pointInFloat(float, canvasX, canvasY);
}

/**
 * @param {import('./imageEditorDocument').EditorLayer} layer
 * @returns {boolean}
 */
export function editorTextLayerHasLiveParams(layer) {
  return isEditorTextLayer(layer);
}
